// SEO utilities for managing canonical URLs and meta tags
export class SEOUtils {
  private static baseURL = 'https://tnsewa.com';

  /**
   * Update the canonical URL for the current page
   * @param path - The current path (e.g., '/about', '/courses/mbbs')
   */
  static updateCanonicalURL(path: string): void {
    // Remove existing canonical tag or update existing one
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    
    if (!canonical) {
      // Create new canonical tag if it doesn't exist
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    
    // Update the href
    canonical.href = `${this.baseURL}${path === '/' ? '' : path}`;
  }

  /**
   * Update page title dynamically
   * @param title - The page title
   */
  static updateTitle(title: string): void {
    document.title = title;

    const titleMeta = document.querySelector('meta[name="title"]');
    if (titleMeta) {
      titleMeta.setAttribute('content', title);
    }
    
    // Update og:title meta tag
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', title);
    }
    
    // Update twitter:title meta tag
    const twitterTitle = document.querySelector('meta[property="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', title);
    }
  }

  /**
   * Update page description dynamically
   * @param description - The page description
   */
  static updateDescription(description: string): void {
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', description);
    }

    // Update og:description meta tag
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', description);
    }
    
    // Update twitter:description meta tag
    const twitterDescription = document.querySelector('meta[property="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute('content', description);
    }
  }

  static updateSocialURLs(path: string): void {
    const url = `${this.baseURL}${path === '/' ? '' : path}`;
    ['meta[property="og:url"]', 'meta[property="twitter:url"]'].forEach((selector) => {
      const meta = document.querySelector(selector);
      if (meta) {
        meta.setAttribute('content', url);
      }
    });
  }

  /**
   * Update all SEO meta tags for a page
   * @param options - SEO options for the page
   */
  static updatePageSEO(options: {
    path: string;
    title: string;
    description: string;
  }): void {
    this.updateCanonicalURL(options.path);
    this.updateSocialURLs(options.path);
    this.updateTitle(options.title);
    this.updateDescription(options.description);
  }
}

// Page-specific SEO configurations
export const pagesSEO = {
  '/': {
    title: 'TN SEWA | MBBS, Engineering & Study Abroad Admissions',
    description: 'TN SEWA provides trusted MBBS, BDS, engineering, scholarship, and overseas education guidance for students across Tamil Nadu and India.'
  },
  '/mbbs': {
    title: 'MBBS Admission Guidance in Tamil Nadu | TN SEWA',
    description: 'Find the right MBBS college with TN SEWA. Get NEET counselling, eligibility, documentation, and admission guidance for medical colleges across Tamil Nadu and India.'
  },
  '/engineering': {
    title: 'Engineering Admission Guidance | BTech & BE | TN SEWA',
    description: 'TN SEWA helps students choose BTech and BE courses, compare engineering colleges, understand eligibility, and complete admissions across Tamil Nadu.'
  },
  '/bds': {
    title: 'BDS Admission Guidance | Dental Colleges | TN SEWA',
    description: 'Get BDS admission guidance from TN SEWA, including NEET counselling, dental college selection, documentation, and application support.'
  },
  '/veterinary': {
    title: 'Veterinary Science Admission | BVSc Consultancy | TN SEWA',
    description: 'TN SEWA provides BVSc admission guidance, eligibility support, and veterinary college counselling across India.'
  },
  '/pharmacy': {
    title: 'Pharmacy Admission Consultancy | BPharm Guidance | TN SEWA',
    description: 'Get BPharm and DPharm course guidance from TN SEWA, including college selection, eligibility, and admission support.'
  },
  '/nursing': {
    title: 'Nursing Admission Consultancy | BSc Nursing | TN SEWA',
    description: 'TN SEWA helps students with BSc Nursing and GNM college selection, eligibility, applications, and admission guidance.'
  },
  '/arts-science': {
    title: 'Arts & Science Admission | BA, BSc, BCA | TN SEWA',
    description: 'Explore BA, BSc, BCA, and other arts and science courses with TN SEWA admission and college selection guidance.'
  },
  '/polytechnic': {
    title: 'Polytechnic Admission | Diploma Courses | TN SEWA',
    description: 'TN SEWA provides polytechnic and diploma course guidance, eligibility advice, and admission support for technical education.'
  },
  '/scholarships': {
    title: 'Scholarships for Students in Tamil Nadu | TN SEWA',
    description: 'Explore merit, need-based, government, and professional course scholarships. TN SEWA helps students check eligibility and prepare applications.'
  },
  '/abroad-studies': {
    title: 'Overseas Studies Consultancy | Bangalore & Abroad | TN SEWA',
    description: 'Plan your overseas education with TN SEWA. Get university selection, applications, scholarships, visa guidance, and pre-departure support.'
  },
  '/founder': {
    title: 'Meet the Founder | TN SEWA Educational Consultancy',
    description: 'Learn about the vision and team behind TN SEWA, an educational consultancy supporting students with admissions and career guidance.'
  },
  '/about-us': {
    title: 'About TN SEWA | Education & Admission Guidance',
    description: 'Learn how TN SEWA supports students with college selection, admissions, scholarships, career planning, and overseas education.'
  },
  '/consultancy': {
    title: 'Educational Consultancy Services | TN SEWA',
    description: 'TN SEWA offers personalised admission, college selection, scholarship, career planning, and academic guidance.'
  },
  '/services': {
    title: 'Admission & Career Guidance Services | TN SEWA',
    description: 'Access TN SEWA admission guidance, career counselling, documentation support, and academic planning services.'
  },
  '/colleges': {
    title: 'Top Medical & Engineering Colleges | TN SEWA',
    description: 'Explore medical, engineering, and professional college options with TN SEWA guidance for your career goals.'
  },
  '/premium-services': {
    title: 'Premium Admission Consultancy | TN SEWA',
    description: 'Get personalised support from TN SEWA counsellors for college selection, applications, admissions, and career decisions.'
  }
};
